import { ControlBar, ErrorBanner } from '@/components';
import { OTElementId, PATH } from '@/constants';
import { getErrorFromAPI, handleToastMutation } from '@/helpers/toast';
import {
  useCreateRawCourierMapMutation,
  useDeleteRawCourierMapMutation,
  useGetListCourierQuery,
  useGetRawCourierMapsQuery,
  useGetRawCourierOptionsQuery,
  useGetUnrecognizedCarriersQuery,
  useMapAndRetrackRawCourierMutation,
  useUpdateRawCourierMapMutation,
} from '@/redux/api/api.caller';
import commonSlice from '@/redux/features/common.slice';
import { IRawCourierMap } from '@/types/settings';
import {
  Autocomplete,
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  ButtonGroup,
  Card,
  InlineStack,
  Modal,
  Select,
  SkeletonBodyText,
  Text,
} from '@shopify/polaris';
import { DeleteIcon, EditIcon } from '@shopify/polaris-icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface IModalState {
  open: boolean;
  id: number | null;
  from: string;
  to: string;
}

const MaskingArt = () => (
  <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flex: '0 0 72px' }}>
    <rect x="6" y="9" width="60" height="54" rx="8" fill="#f6f6f7" stroke="#e1e1e1" />
    <rect x="16" y="19" width="26" height="6" rx="3" fill="#d9d9d9" />
    <rect x="16" y="30" width="40" height="5" rx="2.5" fill="#ececee" />
    <rect x="16" y="42" width="34" height="13" rx="6.5" fill="#e4e6fb" stroke="#bcc1f6" />
    <circle cx="23" cy="48.5" r="2.2" fill="#8a8fe0" />
    <rect x="29" y="46" width="16" height="5" rx="2.5" fill="#b9bdf3" />
  </svg>
);

const BLANK_MODAL: IModalState = { open: false, id: null, from: '', to: '' };
const MAX_SUGGESTIONS = 50;

const CarrierMapping = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [modal, setModal] = useState<IModalState>(BLANK_MODAL);
  const [toQuery, setToQuery] = useState('');
  const [apiError, setApiError] = useState('');
  const [rules, setRules] = useState<Array<IRawCourierMap>>([]);
  const [loadedRules, setLoadedRules] = useState<Array<IRawCourierMap>>([]);

  const mapsQuery = useGetRawCourierMapsQuery();
  const { data: mapsData, isLoading: isLoadingMaps } = mapsQuery;
  const { data: unrecognizedData, isLoading: isLoadingUnrecognized } = useGetUnrecognizedCarriersQuery();
  const { data: optionsData } = useGetRawCourierOptionsQuery({ page: 1, perPage: 500 });
  const { data: courierData } = useGetListCourierQuery({ page: 1, perPage: 500 });
  const [createMap, createStatus] = useCreateRawCourierMapMutation();
  const [updateMap, updateStatus] = useUpdateRawCourierMapMutation();
  const [deleteMap, deleteStatus] = useDeleteRawCourierMapMutation();
  const [mapAndRetrack, mapAndRetrackStatus] = useMapAndRetrackRawCourierMutation();

  const tempIdRef = useRef(-1);
  const isSavingRef = useRef(false);

  const fetchedMaps = mapsData?.data;
  const unrecognized = unrecognizedData?.data ?? [];
  const couriers = useMemo(() => courierData?.data?.couriers ?? [], [courierData]);
  const isEdit = modal.id !== null;

  const { added, deletedIds, updated, isDirty } = useMemo(() => {
    const added = rules.filter((r) => r.id < 0);
    const ruleIds = rules.map((r) => r.id);
    const deletedIds = loadedRules.filter((b) => !ruleIds.includes(b.id)).map((b) => b.id);
    const updated = rules.filter((r) => {
      if (r.id < 0) return false;
      const base = loadedRules.find((b) => b.id === r.id);
      return !!base && base.mapCourier !== r.mapCourier;
    });
    return { added, deletedIds, updated, isDirty: added.length > 0 || deletedIds.length > 0 || updated.length > 0 };
  }, [rules, loadedRules]);

  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    if (!fetchedMaps) return;
    if (isSavingRef.current || isDirtyRef.current) return;
    setRules(fetchedMaps);
    setLoadedRules(fetchedMaps);
  }, [fetchedMaps]);

  const displayOf = (name: string) => couriers.find((c) => c.name === name)?.display_name || name;

  const fromOptions = useMemo(() => {
    const used = new Set(rules.map((r) => r.rawCourier));
    return (optionsData?.data?.rawCouriers ?? [])
      .filter(({ rawCourier }) => rawCourier === modal.from || !used.has(rawCourier))
      .map(({ rawCourier }) => ({ label: rawCourier, value: rawCourier }));
  }, [optionsData, rules, modal.from]);

  const toOptions = useMemo(() => {
    const q = toQuery.trim().toLowerCase();
    return couriers
      .filter((c) => !q || c.display_name.toLowerCase().includes(q))
      .slice(0, MAX_SUGGESTIONS)
      .map((c) => ({ value: c.name, label: c.display_name }));
  }, [couriers, toQuery]);

  const closeModal = () => {
    setModal(BLANK_MODAL);
    setToQuery('');
  };

  const openAdd = () => {
    setApiError('');
    setModal({ ...BLANK_MODAL, open: true });
  };

  const openMap = (rawCourier: string) => () => {
    setApiError('');
    setModal({ ...BLANK_MODAL, open: true, from: rawCourier });
  };

  const openEdit = (item: IRawCourierMap) => () => {
    setApiError('');
    setToQuery(displayOf(item.mapCourier));
    setModal({ open: true, id: item.id, from: item.rawCourier, to: item.mapCourier });
  };

  const stuck = isEdit ? undefined : unrecognized.find((u) => u.rawCourier === modal.from);
  const stuckCount = stuck?.notTrackingCount ?? 0;
  const quotaCount = stuck?.quotaCount ?? 0;
  const isRetrack = !isEdit && stuckCount > 0;
  const canSubmit = !!modal.from && !!modal.to;
  const submitLabel = isEdit ? 'Save' : isRetrack ? `Re-track ${stuckCount} shipments` : 'Add rule';

  const isOk = (res: any) => 'data' in res && res.data?.state === 1;

  const handleRetrack = async () => {
    setApiError('');
    const res = await mapAndRetrack({ rawCourier: modal.from, name: modal.to, display_name: displayOf(modal.to) });
    if (!isOk(res)) return setApiError(getErrorFromAPI(res));
    dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));
    closeModal();
  };

  const handleSubmitModal = () => {
    if (!canSubmit) return;
    if (isRetrack) {
      void handleRetrack();
      return;
    }
    if (isEdit) {
      setRules((prev) => prev.map((r) => (r.id === modal.id ? { ...r, mapCourier: modal.to } : r)));
    } else {
      setRules((prev) => [{ id: tempIdRef.current, rawCourier: modal.from, mapCourier: modal.to }, ...prev]);
      tempIdRef.current -= 1;
    }
    closeModal();
  };

  const handleRemoveRow = (id: number) => () => {
    setApiError('');
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  const handleDiscard = () => {
    setRules(loadedRules);
    setApiError('');
  };

  const handleSave = async () => {
    isSavingRef.current = true;
    try {
      const delResults = await Promise.all(deletedIds.map((id) => deleteMap(id)));
      const createResults = await Promise.all(added.map(({ rawCourier, mapCourier }) => createMap({ rawCourier, mapCourier })));
      const updateResults = await Promise.all(updated.map(({ id, mapCourier }) => updateMap({ id, mapCourier })));

      const allResults = [...delResults, ...createResults, ...updateResults];
      const allOk = allResults.length > 0 && allResults.every(isOk);

      const fresh = await mapsQuery.refetch();
      const serverList = fresh.data?.data ?? [];
      setLoadedRules(serverList);

      if (allOk) {
        setRules(serverList);
        setApiError('');
        const okRes = allResults.find(isOk);
        if (okRes) dispatch(commonSlice.actions.handleToast(handleToastMutation(okRes)));
      } else {
        const next = [...serverList];
        updated.forEach((r, i) => {
          if (!isOk(updateResults[i])) {
            const idx = next.findIndex((x) => x.id === r.id);
            if (idx !== -1) next[idx] = { ...next[idx], mapCourier: r.mapCourier };
          }
        });
        const failedCreates = added.filter((_, i) => !isOk(createResults[i]));
        setRules([...failedCreates, ...next]);
        setApiError(getErrorFromAPI(allResults.find((res) => !isOk(res))));
      }
    } finally {
      isSavingRef.current = false;
    }
  };

  return (
    <BlockStack gap="400">
      <ControlBar
        isFixed
        saveBarId={OTElementId.SaveBarCourierMapping}
        hidden={!isDirty}
        actionGroup={[
          { content: 'Discard', onAction: handleDiscard, primary: false },
          {
            content: 'Save',
            onAction: handleSave,
            primary: true,
            loading: createStatus.isLoading || updateStatus.isLoading || deleteStatus.isLoading,
          },
        ]}
      />
      <Banner tone="info">
        <Text as="p" variant="bodyMd">
          Carrier names come in many variations and aren&apos;t always recognized automatically, so some shipments stop updating.
          Carrier mapping matches those names to a known carrier so tracking keeps working.
        </Text>
      </Banner>
      <ErrorBanner isVisible={!!apiError && !modal.open}>{apiError}</ErrorBanner>

      <Card padding="0">
        <Box padding="300" borderBlockEndWidth="025" borderColor="border">
          <Text as="h2" variant="headingMd">
            Unrecognized carriers
          </Text>
        </Box>
        <Box padding="400">
          <BlockStack gap="200">
            {isLoadingUnrecognized ? (
              <SkeletonBodyText />
            ) : unrecognized.length === 0 ? (
              <Text as="p" variant="bodySm" tone="subdued">
                No unrecognized carriers right now. Shipments with a carrier that can&apos;t be recognized will appear here to
                map.
              </Text>
            ) : (
              <>
                <Text as="p" variant="bodySm" tone="subdued">
                  These carrier names on your shipments aren&apos;t recognized, so tracking is stuck. Map each one to track it
                  correctly.
                </Text>
                {unrecognized.map(({ rawCourier, notTrackingCount }, index) => (
                  <Box
                    key={rawCourier}
                    paddingBlockStart="300"
                    paddingBlockEnd="300"
                    borderBlockStartWidth={index === 0 ? undefined : '025'}
                    borderColor="border"
                  >
                    <InlineStack align="space-between" blockAlign="center">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="span" variant="bodyMd">
                          {rawCourier}
                        </Text>
                        <Badge tone="warning">{`${notTrackingCount} shipments not tracking`}</Badge>
                      </InlineStack>
                      <Button onClick={openMap(rawCourier)}>Map</Button>
                    </InlineStack>
                  </Box>
                ))}
              </>
            )}
          </BlockStack>
        </Box>
      </Card>

      <Card padding="0">
        <Box padding="300" borderBlockEndWidth="025" borderColor="border">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">
              Carrier mapping rules
            </Text>
            <Button variant="primary" onClick={openAdd}>
              Add rule
            </Button>
          </InlineStack>
        </Box>
        <Box padding="400">
          <BlockStack gap="200">
            <Text as="p" variant="bodySm" tone="subdued">
              Map a carrier name from your shipments to the carrier used to fetch tracking updates.
            </Text>
            {isLoadingMaps ? (
              <SkeletonBodyText />
            ) : rules.length === 0 ? (
              <Text as="p" variant="bodySm" tone="subdued">
                No rules yet. Add a rule to map a carrier name — for example, DPD Direct → DPD (UK).
              </Text>
            ) : (
              rules.map((item, index) => (
                <Box
                  key={item.id}
                  paddingBlockStart="300"
                  paddingBlockEnd="300"
                  borderBlockStartWidth={index === 0 ? undefined : '025'}
                  borderColor="border"
                >
                  <InlineStack align="space-between" blockAlign="center">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="span" variant="bodyMd">
                        {item.rawCourier}
                      </Text>
                      <Text as="span" variant="bodyMd" tone="subdued">
                        →
                      </Text>
                      <Text as="span" variant="bodyMd">
                        {displayOf(item.mapCourier)}
                      </Text>
                    </InlineStack>
                    <ButtonGroup>
                      <Button variant="plain" icon={DeleteIcon} accessibilityLabel="Delete" onClick={handleRemoveRow(item.id)} />
                      <Button variant="plain" icon={EditIcon} accessibilityLabel="Edit" onClick={openEdit(item)} />
                    </ButtonGroup>
                  </InlineStack>
                </Box>
              ))
            )}
          </BlockStack>
        </Box>
      </Card>

      <Card>
        <InlineStack gap="400" blockAlign="center" wrap={false}>
          <MaskingArt />
          <div style={{ flex: 1, minWidth: 0 }}>
            <BlockStack gap="050">
              <Text as="h3" variant="headingSm">
                Need to hide or rename a carrier?
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Carrier masking changes what customers see on the tracking page — like showing &quot;Standard Shipping&quot;
                instead of the real carrier name.
              </Text>
            </BlockStack>
          </div>
          <Button onClick={() => navigate(PATH.BLACKLIST)}>Set up masking</Button>
        </InlineStack>
      </Card>

      <Modal
        open={modal.open}
        onClose={closeModal}
        title={isEdit ? 'Edit carrier mapping' : 'Map carrier'}
        primaryAction={{
          content: submitLabel,
          onAction: handleSubmitModal,
          disabled: !canSubmit,
          loading: mapAndRetrackStatus.isLoading,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: closeModal }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <ErrorBanner isVisible={!!apiError}>{apiError}</ErrorBanner>
            <Select
              label="Carrier on your shipments"
              placeholder="Choose a carrier"
              options={fromOptions}
              value={modal.from}
              onChange={(from) => setModal((prev) => ({ ...prev, from }))}
              disabled={isEdit}
            />
            <Autocomplete
              options={toOptions}
              selected={modal.to ? [modal.to] : []}
              onSelect={([to]) => {
                setModal((prev) => ({ ...prev, to }));
                setToQuery(displayOf(to));
              }}
              textField={
                <Autocomplete.TextField
                  label="Track as"
                  placeholder="Choose a carrier"
                  autoComplete="off"
                  helpText="The carrier used to fetch tracking updates"
                  value={toQuery}
                  onChange={(v) => {
                    setToQuery(v);
                    if (modal.to) setModal((prev) => ({ ...prev, to: '' }));
                  }}
                />
              }
            />
            {isRetrack && !!modal.to && (
              <Text as="p" variant="bodySm" tone="subdued">
                {quotaCount === 0
                  ? 'No extra quota used — these shipments were already counted.'
                  : `Uses up to ${quotaCount} of your monthly quota (estimated).`}
              </Text>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </BlockStack>
  );
};

export default CarrierMapping;
